/**
 * The Portfolio Planner input form.
 *
 * Collects exactly what `POST portfolio_planner/generate/` takes — client, one
 * or more goals, any liabilities, and the retirement SWP — and costs the plan
 * live as it is filled in. Dropdown values and every constant behind the badges
 * come from `bootstrap`, never a local constant, so nothing here can drift from
 * the plan the engine later returns.
 *
 * Ported from the user platform's PortfolioPlanner/PlannerForm.tsx. Two
 * differences, both structural rather than behavioural:
 *
 *  • The bootstrap call is owned by the page (the report needs its `config`
 *    too) and handed down, instead of being pulled from a React Query cache
 *    this project does not have.
 *  • The signed-out gate is gone. An admin is always signed in; what replaces
 *    it is the page's own investor selection.
 */

import React from "react";
import AddIcon from "@mui/icons-material/Add";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { headline, rupee } from "../../../Helpers/portfolioPlanner/format";
import type {
  PlannerBootstrap,
  PlannerFrequency,
  PlannerGenerateRequest,
  PlannerInvestorType,
  PlannerRiskProfile,
  PlannerTaxBracket,
} from "../../../Helpers/portfolioPlanner/types";
import {
  ChoiceField,
  DateField,
  InlineLoader,
  MoneyField,
  NumberField,
  SelectField,
  TextField,
  ToggleField,
  WholeNumberField,
} from "./PlannerFields";
import PlannerGoalRow from "./PlannerGoalRow";
import PlannerLiabilityRow from "./PlannerLiabilityRow";
import { usePlannerForm } from "./usePlannerForm";
import styles from "./portfolio-planner.module.css";

interface PlannerFormProps {
  bootstrap: PlannerBootstrap;
  /** The investor's own name, used when the KYC prefill carries none. */
  fallbackName?: string;
  generating: boolean;
  generateError: string;
  onGenerate: (request: PlannerGenerateRequest) => void;
}

const SectionCard = ({
  index,
  title,
  note,
  children,
}: {
  index: number;
  title: string;
  note?: string;
  children: React.ReactNode;
}) => (
  <section className={styles.card}>
    <div className={styles.cardHead}>
      <span className={styles.cardIndex}>{String(index).padStart(2, "0")}</span>
      <h3 className={styles.cardTitle}>{title}</h3>
    </div>
    <div className={styles.cardBody}>
      {note ? <p className={styles.sectionNote}>{note}</p> : null}
      {children}
    </div>
  </section>
);

const PlannerForm = ({
  bootstrap,
  fallbackName,
  generating,
  generateError,
  onGenerate,
}: PlannerFormProps) => {
  const config = bootstrap.config;
  const options = bootstrap.options;
  const prefill = bootstrap.prefill;

  const form = usePlannerForm(config, prefill);
  const {
    client,
    goals,
    liabilities,
    swp,
    goalBadges,
    liabilityBadges,
    totals,
    validation,
    today,
  } = form;

  const handleGenerate = () => {
    if (!validation.canGenerate || generating) return;
    const request = form.toRequest();
    if (!request.client.name || request.client.name === "Client") {
      request.client.name = (fallbackName || "Client").trim() || "Client";
    }
    onGenerate(request);
  };

  const shortfall = totals.surplusGap < 0;

  return (
    <div className={styles.layout}>
      <div className={styles.formColumn}>
        <SectionCard index={1} title="About this investor">
          <div className={styles.grid}>
            <TextField
              label="Name"
              value={client.name}
              placeholder={fallbackName || "Full name"}
              onChange={(value) => form.patchClient({ name: value })}
            />
            <WholeNumberField
              label="Age — years"
              value={client.ageY}
              placeholder="e.g. 34"
              suffix="yr"
              onChange={(value) => form.patchClient({ ageY: value })}
            />
            <WholeNumberField
              label="Age — months"
              value={client.ageM}
              placeholder="0–11"
              maxLength={2}
              suffix="mo"
              onChange={(value) => form.patchClient({ ageM: value })}
            />
            <SelectField<PlannerInvestorType>
              label="Investor type"
              value={client.type}
              options={options.investorType}
              onChange={(value) => form.patchClient({ type: value })}
            />
            <SelectField<PlannerTaxBracket>
              label="Tax bracket"
              value={client.taxBracket}
              options={options.taxBracket}
              formatOption={(option) => (option === 0 ? "No tax" : `${option}%`)}
              tag={prefill && prefill.taxBracketIsSuggestion ? "suggested" : undefined}
              hint={
                prefill && prefill.taxBracketIsSuggestion
                  ? "Suggested from the investor's KYC income band — confirm it with them; it drives the taxation rules."
                  : undefined
              }
              onChange={(value) => form.patchClient({ taxBracket: value })}
            />
            <ToggleField
              label="Shariah-compliant only"
              checked={client.shariah}
              onChange={(checked) => form.patchClient({ shariah: checked })}
            />

            <ChoiceField<PlannerRiskProfile>
              label="Risk profile"
              value={client.risk}
              options={[
                {
                  value: "Aggressive",
                  title: "Aggressive",
                  detail: "Growth first; accepts deeper falls along the way.",
                },
                {
                  value: "Conservative",
                  title: "Conservative",
                  detail: "Protects capital; steadier, lower expected return.",
                },
              ]}
              onChange={(value) => form.patchClient({ risk: value })}
            />

            <ChoiceField<"first" | "seasoned">
              label="Investing experience"
              value={client.firstTimer ? "first" : "seasoned"}
              options={[
                {
                  value: "first",
                  title: "First-time investor",
                  detail: "Keeps to large-cap, multi-cap and hybrid funds.",
                },
                {
                  value: "seasoned",
                  title: "Seasoned investor",
                  detail: "The full fund universe is available.",
                },
              ]}
              onChange={(value) => form.patchClient({ firstTimer: value === "first" })}
            />
          </div>

          <p className={styles.subHead}>What is available to invest</p>
          <div className={styles.grid}>
            <MoneyField
              label="Monthly surplus"
              value={client.monthlySurplus}
              placeholder="50,000"
              onChange={(value) => form.patchClient({ monthlySurplus: value })}
            />
            <MoneyField
              label="One-time lumpsum"
              value={client.lumpsum}
              placeholder="10,00,000"
              hint="Deployed into the longest-tenure goal."
              onChange={(value) => form.patchClient({ lumpsum: value })}
            />
            <ToggleField
              label="Invest the lumpsum through a bond"
              checked={client.investBonds}
              disabled={client.shariah}
              hint={
                client.shariah
                  ? "Locked off — Shariah mode excludes interest-bearing bonds."
                  : "Coupons are swept into an SIP as they are paid. Bond lines are raised from the bond desk, not from this screen."
              }
              onChange={(checked) => form.patchClient({ investBonds: checked })}
            />
          </div>
        </SectionCard>

        <SectionCard
          index={2}
          title="Goals"
          note="Add as many as needed. The longest-tenure goal receives the one-time lumpsum; the rest are funded by SIP."
        >
          <div className={styles.rowStack}>
            {goals.map((goal, index) => (
              <PlannerGoalRow
                key={goal.key}
                goal={goal}
                index={index}
                badges={goalBadges[goal.key]}
                options={options}
                config={config}
                today={today}
                canRemove={goals.length > 1}
                onPatch={(patch) => form.patchGoal(goal.key, patch)}
                onRemove={() => form.removeGoal(goal.key)}
              />
            ))}
          </div>
          <button type="button" className={styles.addButton} onClick={form.addGoal}>
            <AddIcon style={{ fontSize: 17 }} aria-hidden="true" />
            Add another goal
          </button>
        </SectionCard>

        <SectionCard
          index={3}
          title="Loans and EMIs"
          note="Optional. For each loan the plan sizes a small SIP — 10% of the EMI — and shows whether it grows enough to cancel the interest paid."
        >
          {liabilities.length ? (
            <div className={styles.rowStack}>
              {liabilities.map((liability, index) => (
                <PlannerLiabilityRow
                  key={liability.key}
                  liability={liability}
                  index={index}
                  badges={liabilityBadges[liability.key]}
                  options={options}
                  onPatch={(patch) => form.patchLiability(liability.key, patch)}
                  onRemove={() => form.removeLiability(liability.key)}
                />
              ))}
            </div>
          ) : (
            <p className={styles.emptyNote}>
              No loans added. Skip this if the investor has none.
            </p>
          )}
          <button
            type="button"
            className={styles.addButton}
            onClick={form.addLiability}
          >
            <AddIcon style={{ fontSize: 17 }} aria-hidden="true" />
            Add a loan
          </button>
        </SectionCard>

        <SectionCard
          index={4}
          title="Retirement income"
          note="Optional. Fill in a withdrawal or a lumpsum at retirement and the plan projects how long the corpus lasts, with withdrawals rising each year with inflation."
        >
          <div className={styles.grid}>
            <DateField
              label="Withdrawals start"
              value={swp.startDate}
              tag={!swp.dateTouched && swp.startDate ? "auto" : undefined}
              hint={
                !swp.dateTouched && swp.startDate
                  ? "Following the Retirement goal's timeline."
                  : undefined
              }
              onChange={(value) => form.patchSwp({ startDate: value })}
            />
            <NumberField
              label="Expected return"
              value={swp.returnPct}
              placeholder={String(config.swpDefault.returnPct)}
              step={0.1}
              suffix="%"
              onChange={(value) => form.patchSwp({ returnPct: value })}
            />
            <NumberField
              label="Inflation"
              value={swp.inflationPct}
              placeholder={String(config.swpDefault.inflationPct)}
              step={0.1}
              suffix="%"
              onChange={(value) => form.patchSwp({ inflationPct: value })}
            />
            <MoneyField
              label="Lumpsum at retirement"
              value={swp.lumpsum}
              placeholder="20,00,000"
              onChange={(value) => form.patchSwp({ lumpsum: value })}
            />
            <MoneyField
              label="Pension income"
              value={swp.pension}
              placeholder="15,000"
              hint="Netted off the withdrawal each month."
              onChange={(value) => form.patchSwp({ pension: value })}
            />
            <MoneyField
              label="Withdrawal amount"
              value={swp.withdrawal}
              placeholder="60,000"
              onChange={(value) => form.patchSwp({ withdrawal: value })}
            />
            <SelectField<PlannerFrequency>
              label="Withdrawal frequency"
              value={swp.frequency}
              options={options.withdrawalFrequency}
              onChange={(value) => form.patchSwp({ frequency: value })}
            />
          </div>
          {swp.withdrawal > 0 ? (
            <p className={styles.inlineNote}>
              <InfoOutlinedIcon style={{ fontSize: 16, flex: "0 0 auto" }} aria-hidden="true" />
              <span>
                {swp.frequency === "Monthly" ? (
                  swp.pension >= swp.withdrawal ? (
                    <>
                      The pension covers the whole withdrawal, so the corpus is
                      never drawn down — the surplus of{" "}
                      {rupee(swp.pension - swp.withdrawal)} a month can itself be
                      invested as an additional SIP.
                    </>
                  ) : (
                    <>
                      Net withdrawal from the corpus ≈{" "}
                      {rupee(swp.withdrawal - swp.pension)} a month (
                      {rupee(swp.withdrawal)} less {rupee(swp.pension)} of
                      pension), rising with inflation each year.
                    </>
                  )
                ) : (
                  <>
                    Net withdrawal ≈ {rupee(swp.withdrawal)} per{" "}
                    {swp.frequency.toLowerCase()}, rising with inflation each
                    year. Pension is netted off only on monthly withdrawals.
                  </>
                )}
              </span>
            </p>
          ) : null}
        </SectionCard>
      </div>

      {/*
        The running summary. Indicative: each goal is costed on its own, before
        the engine applies the lumpsum to the longest one. The last figure is
        the only red on the screen — it flags when the plan asks for more than
        the recorded surplus, and nothing else competes with it for attention.
      */}
      <aside className={styles.rail} aria-label="Plan summary">
        <div className={styles.railHead}>This plan</div>
        <div className={styles.railList}>
          <div className={styles.railItem}>
            <span className={styles.railLabel}>Goals</span>
            <b className={styles.railValue}>{totals.goalCount}</b>
            <span className={styles.railNote}>
              {totals.goalCount ? "ready to plan" : "add a cost and a timeline"}
            </span>
          </div>
          <div className={styles.railItem}>
            <span className={styles.railLabel}>Total at target</span>
            <b className={styles.railValue}>{headline(totals.totalFutureCost)}</b>
            <span className={styles.railNote}>inflation-adjusted</span>
          </div>
          <div className={styles.railItem}>
            <span className={styles.railLabel}>One-time capital</span>
            <b className={styles.railValue}>{headline(totals.lumpsum)}</b>
            <span className={styles.railNote}>
              {client.investBonds ? "via the bond route" : "as a fund lumpsum"}
            </span>
          </div>
          <div
            className={`${styles.railItem} ${shortfall ? styles.railFlagged : ""}`}
          >
            <span className={styles.railLabel}>Indicative monthly</span>
            <b className={styles.railValue}>
              {headline(totals.indicativeMonthlySip)}
            </b>
            <span className={styles.railNote}>
              {client.monthlySurplus > 0
                ? shortfall
                  ? `${rupee(Math.abs(totals.surplusGap))} over the declared surplus`
                  : `${rupee(totals.surplusGap)} of surplus left`
                : "before the lumpsum is applied"}
            </span>
          </div>
        </div>

        <div className={styles.railFoot}>
          {validation.canGenerate ? null : (
            <ul className={styles.problems}>
              {validation.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
          <button
            type="button"
            className={`${styles.primary} ${styles.generate}`}
            disabled={!validation.canGenerate || generating}
            aria-busy={generating || undefined}
            onClick={handleGenerate}
          >
            {generating ? (
              <>
                <InlineLoader />
                Building the plan…
              </>
            ) : (
              <>
                <CalculateOutlinedIcon style={{ fontSize: 17 }} aria-hidden="true" />
                Generate this plan
              </>
            )}
          </button>
          {generating ? (
            <div className={styles.progressTrack} aria-hidden="true">
              <div className={styles.progressIndeterminate} />
            </div>
          ) : null}
          {generating ? (
            <p className={styles.railNote} role="status">
              Pricing against the live fund universe. This is the slow call —
              leave the tab open.
            </p>
          ) : null}
          {generateError ? (
            <p className={styles.errorNote} role="alert">
              {generateError}
            </p>
          ) : null}
        </div>
      </aside>
    </div>
  );
};

export default PlannerForm;
